import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Row } from 'react-flexbox-grid'
import styled, { css } from 'styled-components'
import cronstrue from 'cronstrue/i18n'

import { parseEmails, normalizeEmails, validateEmails } from 'helpers/emailUtils'

import { ContentBlock } from 'components/ContentBlock'
import { Form, Text, Checkbox, Number as NumberInput, TextArea, Select } from 'components/forms'
import { notEmpty } from 'helpers/validators'
import convertTaskMutation from '../../gql/convertAutomationTaskServerFromWindows'
import runTaskMutation from '../../gql/runScheduleTask'
import createAutoClosingTask from '../../gql/createAutoClosingTask'
import updateAutoClosingTask from '../../gql/updateAutoClosingTask'
import getQueryName from 'helpers/getNameOfGqlQuery'
import { useMutation } from 'components/graphql'
import Button from 'components/Button'
import Divider from 'components/Divider'
import styles from './Task.module.scss'
import CashRegisterList from 'components/CashRegisterTree'
import ScheduleModal from './ScheduleModal'

const SuccessMessage = styled.span`
	color: rgba(46, 125, 50, 1);
	font-size: 14px;
	font-weight: 500;
	margin-top: -20px;
	margin-bottom: 10px;
`

const StyledSection = styled.div`
	width: 400px;
	max-width: 100%;
	padding-left: 8px;
	padding-right: 8px;

	${({ big }) => big && css`
		width: 500px;
	`}
`

const StyledTextArea = styled.div`
	& .wrapper {
		display: block !important;
	}
`

function parseQuartzToTime(cronExpr) {
	if(!cronExpr) {
		return ''
	}
	const parts = cronExpr.trim().split(' ')
	const hour = parts[1]
	const minute = parts[0]
	return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`
}

const getCronExpressionLocal = (cronExpression) => {
	const [minutes, cetHours, dayOfMonth, month, dayOfWeek] = cronExpression.split(' ')

	// Return the original CET time without conversion
	// If you want to convert to local time, uncomment the lines below
	/*
	const date = new Date()
	const localTargetHour = Number(cetHours) - getTimezoneOffsetFromCet()

	// Handle hour normalization properly
	let normalizedHour = localTargetHour
	if (normalizedHour < 0) {
		normalizedHour += 24
	} else if (normalizedHour >= 24) {
		normalizedHour -= 24
	}

	return [minutes, normalizedHour, dayOfMonth, month, dayOfWeek].join(' ')
	*/

	return [minutes, cetHours, dayOfMonth, month, dayOfWeek].join(' ')
}

// Utility function to parse cron expression into schedule object
const parseCronToSchedule = (cronExpression) => {
	if (!cronExpression) {
		return {
			hours: '21',
			minutes: '00',
			dayOfWeek: null,
			dayOfMonth: null,
			months: [],
			cronExpression: '0 21 * * ? *',
			scheduleText: ''
		}
	}

	try {
		// Parse cron expression parts: "minutes hours dayOfMonth month dayOfWeek year"
		const parts = cronExpression.split(' ')
		if (parts.length < 5) {
			return {
				hours: '21',
				minutes: '00',
				dayOfWeek: null,
				dayOfMonth: null,
				months: [],
				cronExpression: '0 21 * * ? *',
				scheduleText: ''
			}
		}

		const [minutes, cetHours, dayOfMonth, month, dayOfWeek] = parts
		const cronExpressionLocal = getCronExpressionLocal(cronExpression)
		// Parse months
		let months = []
		if (month && month !== '*') {
			months = month.split(',').map(m => m.trim())
		}

		// Parse day of week or day of month
		let parsedDayOfWeek = null
		let parsedDayOfMonth = null

		if (dayOfWeek && dayOfWeek !== '*' && dayOfWeek !== '?') {
			// Handle multiple days (e.g., "1,4" for Monday and Thursday)
			if (dayOfWeek.includes(',')) {
				// Convert from cron format (0-6, Sunday=0) to UI format (1-7, Sunday=1)
				const dayValues = dayOfWeek.split(',').map(day => {
					return day === '0' ? '1' : String(parseInt(day, 10) + 1)
				})
				parsedDayOfWeek = dayValues
			} else {
				// Single day
				const uiDayOfWeek = dayOfWeek === '0' ? '1' : String(parseInt(dayOfWeek, 10) + 1)
				parsedDayOfWeek = uiDayOfWeek
			}
		} else if (dayOfMonth && dayOfMonth !== '*' && dayOfMonth !== '?') {
			parsedDayOfMonth = dayOfMonth
		}

		// Generate schedule text using cronstrue
		let scheduleText = ''
		try {
			// Try with English locale first, fallback to default if it fails
			scheduleText = cronstrue.toString(cronExpressionLocal, { locale: 'de' })
		} catch (error) {
			try {
				// Fallback to default locale
				scheduleText = cronstrue.toString(cronExpressionLocal)
			} catch (fallbackError) {
				scheduleText = cronExpressionLocal // Use the raw cron expression as fallback
			}
		}

		return {
			hours: cetHours.padStart(2, '0'),
			minutes: minutes.padStart(2, '0'),
			dayOfWeek: parsedDayOfWeek,
			dayOfMonth: parsedDayOfMonth,
			months,
			cronExpression,
			scheduleText
		}
	} catch (error) {
		return {
			hours: '21',
			minutes: '00',
			dayOfWeek: null,
			dayOfMonth: null,
			months: [],
			cronExpression: '0 21 * * ? *',
			scheduleText: ''
		}
	}
}

const AutomationTask = ({ data, itemId, mutations, handleNewRecord, setRefreshData }) => {
	const [isActive, setIsActive] = useState(data?.isActive)
	const [ids, setIds] = useState(data?.taskType === 'EMAIL_CLOSINGS' ? data?.targetLambdaPayload?.emailCashRegisterIds || [] : data?.targetLambdaPayload?.autoCashRegisterIds || [])
	const [scheduleModalVisible, setScheduleModalVisible] = useState(false)
	const [schedule, setSchedule] = useState(parseCronToSchedule(data?.scheduleCron))
	const [selectedTaskType, setSelectedTaskType] = useState(data?.taskType || 'EMAIL_CLOSINGS')
	const [showSuccessMessage, setShowSuccessMessage] = useState(false)
	const formApiRef = useRef()
	const allValsRef = useRef()
	const [hasUserMadeSelection, setHasUserMadeSelection] = useState(false)
	const [runTask] = useMutation(runTaskMutation, {
		onCompleted: () => {
			setShowSuccessMessage(true)
			setTimeout(() => {
				setShowSuccessMessage(false)
			}, 3000)
		}
	})
	const [convertTask, { loading: convertLoading }] = useMutation(convertTaskMutation, {
		onCompleted: mutationData => {
			const mutationName = getQueryName(convertTaskMutation)
			handleNewRecord(mutationData?.[mutationName])
		}
	})
	const [createAutoClosingTaskMutation, { loading: createAutoClosingLoading }] = useMutation(createAutoClosingTask, {
		onCompleted: mutationData => {
			const mutationName = getQueryName(createAutoClosingTask)
			handleNewRecord(mutationData?.[mutationName])
		}
	})
	const [updateAutoClosingTaskMutation, { loading: updateAutoClosingLoading }] = useMutation(updateAutoClosingTask, {
		onCompleted: mutationData => {
			const mutationName = getQueryName(updateAutoClosingTask)
			handleNewRecord(mutationData?.[mutationName])
		}
	})
	const [t] = useTranslation()

	const formData = useMemo(() => ({
		name: data.taskName,
		email: data?.targetLambdaPayload?.email,
		taskType: data?.taskType,
		daysBack: data?.targetLambdaPayload?.daysBack || 0,
		executionTime: parseQuartzToTime(data?.scheduleCron || ''),
		scheduleText: ''
	}), [data])

	const taskTypeOptions = useMemo(() => [
		{ value: 'AUTO_CLOSING', label: t('task_section.strings.daily_closing') },
		{ value: 'AUTO_CLOSING_AND_LOGOUT', label: t('task_section.strings.daily_closing_and_logout') },
		{ value: 'EMAIL_CLOSINGS', label: t('task_section.strings.daily_closing_with_email') },
		{ value: 'EMAIL_SALE_OFFERS_STATISTICS', label: t('task_section.strings.email_sale_offers_statistics') },
		{ value: 'EXPORT_MIRUS', label: t('options_section.strings.export_mirus_data') }
	], [t])

	const handleSubmit = vals => {
		let cronExpression
		cronExpression = schedule.cronExpression

		let finalIds = ids
		if (!data?.taskId && ids.length === 0 && allValsRef.current && !hasUserMadeSelection) {
			finalIds = allValsRef.current
		}

		let fields = {
			isActive: selectedTaskType === 'AUTO_CLOSING_AND_LOGOUT' || selectedTaskType === 'AUTO_CLOSING' ? true : !!isActive,
			scheduleCron: cronExpression,
			taskName: vals.name,
			payload: { empty: true },
			taskType: selectedTaskType
		}
		if(selectedTaskType === 'EMAIL_CLOSINGS') {
			fields.payload = {
				emailClosing: {
					cashRegisterIds: finalIds,
					daysBack: vals?.daysBack,
					email: normalizeEmails(vals?.email)
				}
			}
		}
		if(selectedTaskType === 'EMAIL_SALE_OFFERS_STATISTICS') {
			fields.payload = {
				emailSaleOffers: {
					daysBack: vals?.daysBack,
					address: parseEmails(vals?.email)
				}
			}
		}
		if(selectedTaskType === 'EXPORT_MIRUS') {
			fields.payload = {
				exportMirus: {
					daysBack: Number(vals?.daysBack) || 0
				}
			}
		}

		if(selectedTaskType === 'AUTO_CLOSING_AND_LOGOUT' || selectedTaskType === 'AUTO_CLOSING') {
			const _fields = {
				isActive: selectedTaskType === 'AUTO_CLOSING_AND_LOGOUT' || selectedTaskType === 'AUTO_CLOSING' ? true : !!isActive,
				scheduleCron: cronExpression,
				name: fields.taskName,
				cashRegisterId: finalIds
			}
			if(!data?.taskId) {
				createAutoClosingTaskMutation({
					variables: {
						fields: {
							..._fields,
							taskType: selectedTaskType
						}
					}
				})
			} else {
				updateAutoClosingTaskMutation({
					variables: {
						id: data.taskId,
						fields: {
							..._fields
						}
					}
				})
			}
		} else if(!data?.taskId) {
			mutations.createItem(fields)
		} else {
			// updateScheduleTask needs taskType as a separate variable (not part of the fields input)
			mutations.updateItem(fields, {
				variables: {
					id: data.taskId,
					taskType: selectedTaskType,
					fields: {
						taskName: fields.taskName,
						isActive: fields.isActive,
						scheduleCron: fields.scheduleCron,
						payload: fields.payload
					}
				}
			})
		}
		setRefreshData(true)
	}

	const handleActiveChange = e => {
		const val = e.target.checked
		setIsActive(val)
	}

	const handleConvert = () => {
		convertTask({
			variables: { id: itemId }
		})
	}

	const handleScheduleSave = (newSchedule) => {
		setSchedule(newSchedule)
		setScheduleModalVisible(false)
		formApiRef.current.setValue('scheduleText', newSchedule.scheduleText)
	}

	const handleCashRegisterChange = (newIds) => {
		setHasUserMadeSelection(true)
		setIds(newIds)
	}

	// Update form field when schedule changes
	useEffect(() => {
		if (formApiRef.current && schedule.scheduleText) {
			formApiRef.current.setValue('scheduleText', schedule.scheduleText)
		}
	}, [schedule.scheduleText])

	if(data?.isWindows) {
		return(
			<ContentBlock>
				<p>{t('options_section.strings.convert_task')}</p>
				<Divider />
				<Button
					text={t('options_section.strings.convert')}
					onClick={handleConvert}
					loading={convertLoading}
				/>
			</ContentBlock>
		)
	}

	const onRunTask = () => {
		runTask({
			variables: { runScheduleTaskId: data?.taskId, taskType: data?.taskType }
		})
	}

	return(
		<>
			<Form initialValues={formData} onSubmit={handleSubmit} apiRef={formApiRef}>
				<ContentBlock label={t('options_section.strings.general')}>
					<Row>
						<StyledSection>
							<Row className={styles.row}>
								<Text
									field='name'
									label={t('task_section.strings.task_description')}
									{...notEmpty()}
									validateOnChange
								/>
								<Checkbox
									noForm
									checked={isActive}
									onChange={handleActiveChange}
									label={t('common.fields.isActive')}
									inline
								/>
							</Row>
							<Row className={styles.row} style={{ marginTop: 10 }}>
								<Select
									field='taskType'
									label={t('task_section.strings.task_type')}
									options={taskTypeOptions}
									value={selectedTaskType}
									onChange={({ value }) => {
										setSelectedTaskType(value)

										if (value !== 'EMAIL_CLOSINGS' && value !== 'EXPORT_MIRUS') {
											setIsActive(true)
										}
									}}
									{...notEmpty()}
									disabled={!!data?.taskId}
									validateOnChange
								/>
								{data?.taskId && selectedTaskType === 'EMAIL_CLOSINGS' && (
									<>
										<Button onClick={onRunTask} text={t('task_section.strings.run_task')} />
										{showSuccessMessage && (
											<SuccessMessage>
												{t('task_section.strings.task_run_successfully')}
											</SuccessMessage>
										)}
									</>
								)}
							</Row>
							{(selectedTaskType === 'EMAIL_CLOSINGS' || selectedTaskType === 'EMAIL_SALE_OFFERS_STATISTICS') && <Row className={styles.row}>
								<TextArea
									field='email'
									label={t('options_section.fields.email_list')}
									validate={val => {
										if(!validateEmails(val)) {
											return t('validators.invalid_value')
										}
									}}
									validateOnChange
									rows={3}
								/>
							</Row>}
						</StyledSection>
					</Row>
				</ContentBlock>

				<ContentBlock related label={t('common.strings.settings')}>
					<Row>
						{selectedTaskType && <StyledSection>
							{(selectedTaskType === 'EMAIL_CLOSINGS' || selectedTaskType === 'EMAIL_SALE_OFFERS_STATISTICS' || selectedTaskType === 'EXPORT_MIRUS') && (
								<Row className={styles.row}>
									<div style={{ width: '160px' }}>
										<NumberInput
											field='daysBack'
											label={t('task_section.strings.days_back')}
											min={0}
											max={365}
											style={{ width: '160px', maxWidth: '100%' }}
											validate={val => {
												if (!`${val ?? ''}` || val < 0) {
													return t('validators.min_value', { min: 0 })
												}
												if (val > 365) {
													return t('validators.max_value', { max: 365 })
												}
											}}
											validateOnChange
										/>
									</div>

								</Row>
							)}
							<StyledTextArea>
								<TextArea
									field='scheduleText'
									label={t('task_section.strings.execution_schedule')}
									readOnly
									{...notEmpty()}
									validateOnChange
									style={{ width: '100%', display: 'block', maxWidth: 'unset' }}
								/>
							</StyledTextArea>
						</StyledSection>}
					</Row>
					<Divider/>
					<Button
						onClick={() => setScheduleModalVisible(true)}
						text={t('task_section.strings.configurate')}
					/>
				</ContentBlock>

				{selectedTaskType !== 'EMAIL_SALE_OFFERS_STATISTICS' && selectedTaskType !== 'EXPORT_MIRUS' &&
				<ContentBlock related label={t('task_section.strings.cash_registers')}>
					<Row>
						<StyledSection big>
							<CashRegisterList
								allValsRef={allValsRef}
								startConcealed
								startAllActive={!data?.taskId && ids.length === 0}
								activeNodesArr={ids}
								hideNoCRBranches
								cardsInRow={1}
								onChange={handleCashRegisterChange}
								onChangeDelay={0}
							/>
						</StyledSection>
					</Row>
				</ContentBlock>}
				<ContentBlock related>
					<Button
						type='submit'
						text={data?.taskId ? t('product_section.strings.update') : t('common.buttons.create')}
						loading={selectedTaskType === 'AUTO_CLOSING_AND_LOGOUT' || selectedTaskType === 'AUTO_CLOSING' ? (createAutoClosingLoading || updateAutoClosingLoading) : false}
					/>
				</ContentBlock>
			</Form>

			{scheduleModalVisible && (
				<ScheduleModal
					visible={scheduleModalVisible}
					onCancel={() => setScheduleModalVisible(false)}
					onSave={handleScheduleSave}
					initialSchedule={schedule}
				/>
			)}
		</>
	)
}

export default AutomationTask
